import { getAuthHeaders } from '../../../actions/authActions';
import tempMeeApi from '../../../apis/tempMee';

/**
 * The review a professional left about the office for one shift.
 *
 * The Job Detail Rating tab's office-side card. Where `getOfficeReviews` lists
 * every review an office has received, this answers for a single job — and
 * since `(professional_id, job_id)` is unique there is at most one.
 *
 * Only the office-side card comes through here. The professional-side card on
 * that same tab shows the *office's* review of the professional, which
 * organization-service does not hold at all — `location_review` is
 * professional→office only — so it stays on the monolith's `admin/jobs/{id}`
 * payload.
 *
 * Deliberately dumb transport: a 404 rejects like any other status. Turning it
 * into "no review" is the service's job, because that decision needs the
 * neighbouring rule that a 403 or 500 must *not* be treated the same way.
 *
 * The route is `/organization-service`, prefixed inline the way the sibling
 * clients prefix theirs, and the token goes in `Authorization` unwrapped as
 * `getAuthHeaders` returns it.
 *
 * @param {Object} params
 * @param {string} params.jobId - The job/shift ObjectID
 * @returns {Promise<import('../types/OfficeReview.api').IGetOfficeReviewByShiftApiResponse>}
 */
export const getOfficeReviewByShift = async ({ jobId }) => {
  // Without this the request would go to `.../shifts/undefined`, which the
  // endpoint answers with the same 404 it uses for a real miss — so a wiring
  // bug would render as "no review submitted" instead of failing.
  if (!jobId) {
    throw new Error('getOfficeReviewByShift requires a jobId');
  }

  const headers = await getAuthHeaders();

  const response = await tempMeeApi.get(
    `/organization-service/admin/office-reviews/shifts/${jobId}`,
    { headers },
  );

  return response.data;
};
